import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, CheckCircle2, Repeat, X, ChefHat, ListChecks, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { ScrollArea } from "../ui/scroll-area";
import api from "../../lib/api";

/**
 * Dialog para revisar la propuesta profesional de IA antes de persistir.
 *
 * Recibe:
 *   - restaurantId
 *   - dish: { dish_id, name }
 *   - proposal: { final_recipe, base_recipes: [{index, name, matched_existing, ...}] }
 *   - onApplied(result): callback tras crear receta final + batch_recipes
 *
 * Por cada base_recipe detectada deja elegir:
 *   - "create": crear como nueva Receta Base (default si matched_existing === null)
 *   - "reuse": usar la existente (default si matched_existing !== null)
 *   - "skip": ignorar (los ingredientes de esa base no entrarán al escandallo)
 */
export default function ProfessionalProposalDialog({
  restaurantId, dish, proposal, open, onOpenChange, onApplied,
}) {
  const initialChoices = (proposal?.base_recipes || []).map((b) => ({
    base_recipe_index: b.index,
    action: b.matched_existing ? "reuse" : "create",
    batch_recipe_id: b.matched_existing?.batch_recipe_id || null,
  }));
  const [choices, setChoices] = useState(initialChoices);
  const [applying, setApplying] = useState(false);

  // 🔧 iter_150 CRITICAL FIX — `useState(initialChoices)` ONLY runs once on
  // first render. When the chef opens this dialog with a DIFFERENT proposal
  // (different dish or regenerated proposal with new bases), `choices`
  // stayed stale with the old (possibly empty) array → apply sent
  // `base_choices: []` → nothing persisted despite the chef clicking OK.
  // This is the root of the 6-times-reported "base never saves" bug.
  // Only re-sync when the proposal identity/content actually changes —
  // using dish_id + base count as a cheap signature. Extracted to a
  // memoized variable so ESLint's exhaustive-deps stays happy.
  const proposalBases = proposal?.base_recipes;
  const proposalBaseCount = (proposalBases || []).length;
  useEffect(() => {
    setChoices(
      (proposalBases || []).map((b) => ({
        base_recipe_index: b.index,
        action: b.matched_existing ? "reuse" : "create",
        batch_recipe_id: b.matched_existing?.batch_recipe_id || null,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal?.dish_id, proposalBaseCount]);

  const setAction = (idx, action) => {
    setChoices((prev) => prev.map((c) =>
      c.base_recipe_index === idx
        ? { ...c, action, batch_recipe_id: action === "reuse"
            ? (proposal.base_recipes.find((b) => b.index === idx)?.matched_existing?.batch_recipe_id || null)
            : null }
        : c,
    ));
  };

  const handleApply = async () => {
    if (!dish?.dish_id || !proposal) return;
    setApplying(true);
    try {
      // 🔧 iter_150 DEFENSE IN DEPTH — Fallback reconstruction:
      // If for any reason `choices` fell out of sync with `proposal`
      // (stale state, race on dialog open, etc.) reconstruct from the
      // live proposal so the apply call NEVER silently sends an empty
      // base_choices array when the chef saw bases in the dialog.
      const propBases = proposal?.base_recipes || [];
      const effective = choices.length === propBases.length
        ? choices
        : propBases.map((b) => ({
            base_recipe_index: b.index,
            action: b.matched_existing ? "reuse" : "create",
            batch_recipe_id: b.matched_existing?.batch_recipe_id || null,
          }));
      const body = {
        dish_id: dish.dish_id,
        final_recipe: proposal.final_recipe,
        base_choices: effective.map((c) => {
          const orig = propBases.find((b) => b.index === c.base_recipe_index);
          const base = {
            action: c.action,
            base_recipe_index: c.base_recipe_index,
            batch_recipe_id: c.batch_recipe_id,
          };
          if (c.action === "create" && orig) {
            base.name = orig.name;
            base.yield_qty = orig.yield_qty;
            base.yield_unit = orig.yield_unit;
            base.shelf_life_days = orig.shelf_life_days;
            base.preparation_notes = orig.preparation_notes;
            base.ingredients = orig.ingredients;
          }
          return base;
        }),
      };
      const result = await api.request(
        `/restaurants/${restaurantId}/ai/recipe-professional/apply`,
        { method: "POST", body: JSON.stringify(body) },
      );
      // iter_149 — surface silent per-base failures with a warning toast so
      // the chef knows the receta base DIDN'T fully persist, instead of
      // the misleading "Receta guardada" that caused 5 repeated bug reports.
      if (Array.isArray(result.errors) && result.errors.length > 0) {
        const failedNames = result.errors.map(e => e.name || "base").join(", ");
        toast.warning(
          `Receta guardada pero ${result.errors.length} receta(s) base fallaron al persistir`,
          {
            description: `No se pudieron crear: ${failedNames}. Revisa el nombre por si ya existe, o reintenta.`,
            duration: 8000,
          },
        );
      } else {
        toast.success(
          `Receta profesional guardada${result.base_recipes_created > 0 ? ` · ${result.base_recipes_created} receta(s) base creada(s)` : ""}${result.base_recipes_reused > 0 ? ` · ${result.base_recipes_reused} reutilizada(s)` : ""}`,
        );
      }
      onApplied?.(result);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.message || "Error guardando la receta");
    } finally { setApplying(false); }
  };

  if (!proposal) return null;
  const { final_recipe, base_recipes } = proposal;
  const directCount = (final_recipe?.direct_ingredients || []).length;
  const basesWithNew = base_recipes?.filter((b) => !b.matched_existing).length || 0;
  const basesMatched = base_recipes?.filter((b) => b.matched_existing).length || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="pro-proposal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" />
            Propuesta profesional — {dish?.name}
          </DialogTitle>
          <DialogDescription>
            La IA ha descompuesto el plato como lo haría una cocina profesional.
            Revisa cada elaboración y confirma antes de guardar.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-3">
          <div className="space-y-4">
            {/* Missing-bases warning — detected when steps reference an
                elaboration (sauce/stock/dough/etc.) that wasn't emitted
                as a base_recipe. Iter_147 anti-hallucination guard. */}
            {Array.isArray(proposal.missing_bases) && proposal.missing_bases.length > 0 && (
              <div className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-950/20 rounded-r-md p-3 space-y-1.5" data-testid="pro-missing-bases-warning">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-300">
                  <AlertTriangle className="w-4 h-4" />
                  La IA mencionó elaboraciones que no propuso como receta base
                </div>
                <ul className="text-xs text-amber-800 dark:text-amber-400 space-y-0.5 pl-6 list-disc">
                  {proposal.missing_bases.map((mb, idx) => (
                    <li key={idx}>
                      <span className="font-medium capitalize">{mb.name}</span>
                      {mb.hint_step && <span className="opacity-70"> — “{mb.hint_step}”</span>}
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-amber-700/90 dark:text-amber-400/80 pl-6">
                  Puedes continuar guardando esta receta y crear las bases manualmente en la pestaña <b>Recetas Base</b>, o cancelar y regenerar la propuesta.
                </p>
              </div>
            )}

            {/* Summary */}
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge variant="secondary" className="gap-1">
                <ChefHat className="w-3 h-3" /> {directCount} ingrediente{directCount === 1 ? "" : "s"} directo{directCount === 1 ? "" : "s"}
              </Badge>
              {basesWithNew > 0 && (
                <Badge className="bg-violet-100 text-violet-700 border-violet-200 hover:bg-violet-100 gap-1">
                  <Sparkles className="w-3 h-3" /> {basesWithNew} base{basesWithNew === 1 ? "" : "s"} nueva{basesWithNew === 1 ? "" : "s"}
                </Badge>
              )}
              {basesMatched > 0 && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 gap-1">
                  <Repeat className="w-3 h-3" /> {basesMatched} reutilizable{basesMatched === 1 ? "" : "s"}
                </Badge>
              )}
              <Badge variant="outline" className="gap-1">
                <ListChecks className="w-3 h-3" /> {(final_recipe?.steps || []).length} paso{(final_recipe?.steps || []).length === 1 ? "" : "s"}
              </Badge>
            </div>

            {/* Each base recipe — confirmation block */}
            {(base_recipes || []).length === 0 && (
              <div className="border border-dashed rounded-lg p-6 text-center space-y-1 text-sm text-muted-foreground" data-testid="pro-no-bases-empty">
                <ChefHat className="w-6 h-6 mx-auto opacity-60" />
                <p className="font-medium">La IA no detectó elaboraciones intermedias</p>
                <p className="text-xs">Este plato se compone de ingredientes directos sin salsas / fondos / masas reutilizables. Puedes confirmar para guardar la receta final tal cual.</p>
              </div>
            )}
            {(base_recipes || []).map((b) => {
              const choice = choices.find((c) => c.base_recipe_index === b.index) || {};
              const matched = b.matched_existing;
              return (
                <div key={b.index} className="border rounded-lg p-4 space-y-3 bg-muted/20"
                  data-testid={`pro-base-${b.index}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold">{b.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        Rinde {b.yield_qty} {b.yield_unit} · {(b.ingredients || []).length} ingrediente{(b.ingredients || []).length === 1 ? "" : "s"}
                        {b.shelf_life_days ? ` · ${b.shelf_life_days} días` : ""}
                      </p>
                    </div>
                    {matched ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 text-[10px]">
                        Ya existe · {matched.name}
                      </Badge>
                    ) : (
                      <Badge className="bg-violet-100 text-violet-700 border-violet-200 hover:bg-violet-100 text-[10px]">
                        Nueva
                      </Badge>
                    )}
                  </div>

                  <RadioGroup value={choice.action} onValueChange={(v) => setAction(b.index, v)} className="space-y-2">
                    {matched ? (
                      <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-background border border-transparent has-[:checked]:border-emerald-300 has-[:checked]:bg-emerald-50">
                        <RadioGroupItem value="reuse" id={`r-reuse-${b.index}`} data-testid={`pro-action-reuse-${b.index}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" /> Reutilizar la existente
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Apunta al batch que ya tienes ({matched.yield_qty} {matched.yield_unit}). No crea duplicado.
                          </p>
                        </div>
                      </label>
                    ) : (
                      <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-background border border-transparent has-[:checked]:border-violet-300 has-[:checked]:bg-violet-50">
                        <RadioGroupItem value="create" id={`r-create-${b.index}`} data-testid={`pro-action-create-${b.index}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium flex items-center gap-1.5">
                            <Sparkles className="w-3.5 h-3.5 text-violet-600" /> Crear como Receta Base
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Se guarda en la pestaña <strong>Recetas Base</strong>. Reutilizable para otros platos.
                          </p>
                        </div>
                      </label>
                    )}
                    <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-background border border-transparent has-[:checked]:border-amber-300 has-[:checked]:bg-amber-50">
                      <RadioGroupItem value="skip" id={`r-skip-${b.index}`} data-testid={`pro-action-skip-${b.index}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium flex items-center gap-1.5">
                          <X className="w-3.5 h-3.5 text-amber-700" /> Omitir
                        </div>
                        <p className="text-xs text-muted-foreground">
                          No se enlaza. Tendrás que añadir los ingredientes manualmente a la receta final.
                        </p>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              );
            })}

            {/* Final recipe summary */}
            <div className="border rounded-lg p-4 bg-background">
              <h4 className="font-semibold flex items-center gap-1.5 text-sm">
                <ChefHat className="w-4 h-4 text-primary" /> Receta final ({final_recipe?.servings} raciones)
              </h4>
              {directCount > 0 && (
                <div className="mt-2 text-xs space-y-0.5">
                  <p className="text-muted-foreground font-medium">Ingredientes directos del montaje:</p>
                  {(final_recipe.direct_ingredients || []).slice(0, 6).map((ing, i) => (
                    <div key={i} className="text-muted-foreground">
                      • {ing.name_guess || ing.ingredient_id || "ingrediente"} — {ing.quantity} {ing.unit}
                    </div>
                  ))}
                  {directCount > 6 && <div className="text-muted-foreground italic">+ {directCount - 6} más</div>}
                </div>
              )}
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={applying}>Cancelar</Button>
          <Button onClick={handleApply} disabled={applying} data-testid="pro-apply-btn" className="gap-2">
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Confirmar y guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
